<template>
  <div class="checkbox" @click="toggleCheckbox">
    <input type="checkbox" ref="inputRef" :id="formField.id" v-model="model" v-bind="$attrs" />
    <Icon class="icon" name="check" size="extra-small" />
  </div>
</template>

<script setup lang="ts">
import { inject, ref, useAttrs } from 'vue';

import { FORM_FIELD_TOKEN } from './form-field/form-field.token';
import Icon from './icon.vue';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const model = defineModel<boolean | null | undefined | string[]>();
const inputRef = ref<HTMLInputElement>();
const formField = inject(FORM_FIELD_TOKEN, { id: attrs.id as string });

function toggleCheckbox(event: MouseEvent) {
  // A click forwarded by a <label for> already toggles the input, so re-clicking it here would undo the toggle.
  if (event.target === inputRef.value) {
    return;
  }

  inputRef.value?.click();
}
</script>

<style scoped lang="scss">
.checkbox {
  width: var(--t-spacing-lg);
  height: var(--t-spacing-lg);

  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-xs);

  input {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    outline: 0;
  }

  .icon {
    visibility: hidden;
  }

  &:has(input:checked) {
    border-color: var(--t-color-neutral-700);
    background-color: var(--t-color-neutral-950);
    color: var(--t-color-neutral-white);

    .icon {
      visibility: visible;
    }
  }

  &:has(input:focus) {
    box-shadow: var(--t-form-field-focus-shadow);
  }

  &:has(input:disabled) {
    opacity: 0.5;
  }

  &:has(:not(input:disabled)) {
    cursor: pointer;
  }
}
</style>
